import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../lib/api';
import ReactMarkdown from 'react-markdown';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ChecklistTab, ComplianceMatrixTab, GanttChartTab } from '../components/ProjectTools';
import SuggestedResponses from '../components/SuggestedResponses';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import {
  ArrowLeft, Upload, FileText, Brain, MessageSquare, CalendarDays, CalendarPlus, Trash2, Send,
  Loader2, CheckCircle, Download, FileSpreadsheet, ScanLine, Users, Handshake,
  UserPlus, BarChart3, AlertTriangle, TrendingUp, X, Wrench, Landmark, Building2,
  ExternalLink, Globe
} from 'lucide-react';
import { toast } from 'sonner';

const moduleLabels = { ofertant: 'Licitatie Publica (SEAP)', autoritate: 'Licitatie Publica (SEAP)', privat: 'Licitatie Privata (RFI/RFP/RFQ)' };
const moduleColors = { ofertant: 'bg-blue-500/10 text-blue-400', autoritate: 'bg-blue-500/10 text-blue-400', privat: 'bg-amber-500/10 text-amber-400' };
function SicapDownloader({ projectId, onDownloaded }) {
  const [cifInput, setCifInput] = useState('');
  const [sicapLoading, setSicapLoading] = useState(false);
  const [sicapResults, setSicapResults] = useState([]);

  const searchSicap = async () => {
    if (!cifInput.trim()) return;
    setSicapLoading(true);
    try {
      const res = await API.get(`/sicap/contracts/${cifInput.replace(/\D/g, '')}/latest`);
      setSicapResults(res.data.contracts || []);
      if (!res.data.contracts?.length) toast.info('Niciun contract gasit pe SICAP');
      else toast.success(`${res.data.contracts.length} contracte gasite`);
    } catch { toast.error('Eroare SICAP (creditele zilnice pot fi epuizate)'); }
    finally { setSicapLoading(false); }
  };

  const importAsDoc = async (contract) => {
    try {
      const text = `SICAP Contract: ${contract.name}\nValoare: ${contract.awardedValue} RON\nSursa: ${contract.source}\nCPV: ${contract.cpvCode}\nFurnizor: ${typeof contract.supplier === 'string' ? contract.supplier : (contract.supplier || []).join(', ')}\nStare: ${contract.acquisitionState}\nData: ${contract.publicationDate}`;
      const blob = new Blob([text], { type: 'text/plain' });
      const fd = new FormData();
      fd.append('file', blob, `SICAP_${contract.noticeNo || 'contract'}.txt`);
      fd.append('project_id', projectId);
      fd.append('doc_category', 'autoritate');
      await API.post('/documents/upload', fd);
      toast.success('Contract SICAP importat ca document!');
      if (onDownloaded) onDownloaded();
    } catch { toast.error('Eroare import'); }
  };

  return (
    <div className="mb-4 p-3 rounded-lg bg-blue-500/5 border border-blue-500/10">
      <div className="flex items-center gap-2 mb-2">
        <Globe className="w-3.5 h-3.5 text-blue-400" />
        <span className="text-xs font-medium text-blue-400">Descarca din SICAP</span>
      </div>
      <div className="flex gap-2 mb-2">
        <Input value={cifInput} onChange={e => setCifInput(e.target.value)} placeholder="CIF autoritate (ex: 4267117)"
          className="bg-zinc-900/50 border-blue-500/20 h-8 text-xs font-mono-data flex-1" onKeyDown={e => e.key === 'Enter' && searchSicap()} data-testid="sicap-cif-download" />
        <Button size="sm" variant="outline" onClick={searchSicap} disabled={sicapLoading} className="h-8 text-xs border-blue-500/20 text-blue-400" data-testid="sicap-download-btn">
          {sicapLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Globe className="w-3 h-3 mr-1" />}
          Cauta
        </Button>
      </div>
      {sicapResults.length > 0 && (
        <div className="max-h-32 overflow-auto space-y-1">
          {sicapResults.slice(0, 5).map((c, i) => (
            <div key={i} className="flex items-center gap-2 p-1.5 rounded bg-zinc-900/30 text-xs">
              <span className="flex-1 truncate">{c.name}</span>
              <span className="text-emerald-400 font-mono-data shrink-0">{(c.awardedValue/1000).toFixed(0)}K</span>
              <button onClick={() => importAsDoc(c)} className="text-blue-400 hover:text-blue-300 text-[10px] shrink-0">Import</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const partnerTypes = [
  { value: 'subcontractant', label: 'Subcontractant' },
  { value: 'asociat', label: 'Asociat' },
  { value: 'tert_sustinator', label: 'Tert Sustinator' },
];

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [generatedForms, setGeneratedForms] = useState([]);
  const [team, setTeam] = useState({ owner: null, members: [] });
  const [partners, setPartners] = useState([]);
  const [scoring, setScoring] = useState([]);
  const [scoring7, setScoring7] = useState([]);
  const [scoring7Loading, setScoring7Loading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generatingForm, setGeneratingForm] = useState('');
  const [scoringLoading, setScoringLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [dlForm, setDlForm] = useState({ title: '', date: '', description: '', priority: 'medium' });
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [partnerForm, setPartnerForm] = useState({ partner_type: 'subcontractant', company_name: '', cui: '', role_description: '', percentage: 0 });
  const [aiResults, setAiResults] = useState([]);
  const [autoGenerating, setAutoGenerating] = useState(false);
  const fileInputRef = useRef(null);
  const fileInputRef2 = useRef(null);
  const chatEndRef = useRef(null);

  const loadAll = async () => {
    try {
      const [pRes, dRes, aRes, dlRes, fRes, tRes, ptRes, sRes, tplRes, aiRes] = await Promise.all([
        API.get(`/projects/${id}`), API.get(`/documents/${id}`),
        API.get(`/analyses/${id}`), API.get(`/deadlines?project_id=${id}`),
        API.get(`/generated-forms/${id}`), API.get(`/projects/${id}/team`),
        API.get(`/partners/${id}`), API.get(`/scoring/${id}`),
        API.get('/form-templates'), API.get(`/ai-results/${id}`)
      ]);
      setProject(pRes.data); setDocuments(dRes.data); setAnalyses(aRes.data);
      setDeadlines(dlRes.data); setGeneratedForms(fRes.data); setTeam(tRes.data);
      setPartners(ptRes.data); setScoring(sRes.data); setTemplates(tplRes.data);
      setAiResults(aiRes.data);
      API.get(`/scoring7/${id}`).then(r => setScoring7(r.data)).catch(() => {});
    } catch { toast.error('Eroare la incarcarea proiectului'); navigate('/projects'); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, [id]);

  // Document handlers
  const handleUpload = async (e, category = 'ofertant') => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append('file', file); fd.append('project_id', id); fd.append('doc_category', category);
        await API.post('/documents/upload', fd);
      }
      toast.success(`${files.length} document(e) incarcate (${category === 'autoritate' ? 'Autoritate' : 'Ofertant'})`); loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare'); }
    finally { setUploading(false); if(fileInputRef.current) fileInputRef.current.value=''; if(fileInputRef2.current) fileInputRef2.current.value=''; }
  };
  const handleDeleteDoc = async (docId) => {
    try { await API.delete(`/documents/file/${docId}`); toast.success('Sters'); loadAll(); } catch { toast.error('Eroare'); }
  };

  // AI auto-generate (ofertant) or verify compliance (autoritate)
  const handleAutoGenerate = async () => {
    setAutoGenerating(true);
    try {
      const endpoint = project?.module_type === 'autoritate' ? `/ai/verify-compliance/${id}` : `/ai/auto-generate/${id}`;
      await API.post(endpoint);
      toast.success(project?.module_type === 'autoritate' ? 'Verificare complianta finalizata!' : 'Generare automata finalizata!');
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare AI'); }
    finally { setAutoGenerating(false); }
  };

  // Analysis handlers
  const handleAnalyze = async () => {
    setAnalyzing(true);
    try { await API.post(`/analyze/${id}`); toast.success('Analiza finalizata!'); loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Eroare'); }
    finally { setAnalyzing(false); }
  };
  const handleExport = async (analysisId, format) => {
    try {
      const res = await API.get(`/export/analysis/${analysisId}?format=${format}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `analiza.${format}`; a.click();
      toast.success(`Export ${format.toUpperCase()} OK`);
    } catch { toast.error('Eroare export'); }
  };

  // Scoring
  const handleScoring = async () => {
    setScoringLoading(true);
    try { await API.post(`/scoring/${id}`); toast.success('Scoring generat!'); loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Eroare scoring'); }
    finally { setScoringLoading(false); }
  };

  const handleScoring7 = async () => {
    setScoring7Loading(true);
    try { await API.post(`/scoring7/${id}`); toast.success('Scoring 7-factori generat!'); loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Eroare scoring'); }
    finally { setScoring7Loading(false); }
  };

  // Forms
  const handleGenerateForm = async (formType) => {
    setGeneratingForm(formType);
    try { await API.post(`/generate/${formType}/${id}`); toast.success('Formular generat!'); loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Eroare'); }
    finally { setGeneratingForm(''); }
  };
  const handleGenerateTemplate = async (code) => {
    setGeneratingForm(code);
    try { await API.post(`/generate/form-template/${id}?template_code=${code}`); toast.success(`${code} generat!`); loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Eroare'); }
    finally { setGeneratingForm(''); }
  };

  // Chat
  const handleChat = async () => {
    if (!chatInput.trim()) return;
    const msg = chatInput.trim();
    setChatMessages(prev => [...prev, { role: 'user', text: msg }]); setChatInput(''); setChatLoading(true);
    try {
      const res = await API.post(`/chat/${id}`, { text: msg });
      setChatMessages(prev => [...prev, { role: 'assistant', text: res.data.response }]);
    } catch (err) { setChatMessages(prev => [...prev, { role: 'assistant', text: 'Eroare: ' + (err.response?.data?.detail || '') }]); }
    finally { setChatLoading(false); setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100); }
  };

  // Deadlines
  const handleAddDeadline = async () => {
    if (!dlForm.title || !dlForm.date) { toast.error('Titlu si data obligatorii'); return; }
    try { await API.post('/deadlines', { ...dlForm, project_id: id }); toast.success('Adaugat'); setDlForm({ title: '', date: '', description: '', priority: 'medium' }); loadAll(); }
    catch { toast.error('Eroare'); }
  };
  const handleToggleDeadline = async (dl) => { try { await API.put(`/deadlines/${dl.id}`, { completed: !dl.completed }); loadAll(); } catch {} };
  const handleDeleteDeadline = async (dlId) => { try { await API.delete(`/deadlines/${dlId}`); loadAll(); } catch {} };
  const [extractingDeadlines, setExtractingDeadlines] = useState(false);
  const handleExtractDeadlines = async () => {
    setExtractingDeadlines(true);
    try {
      const r = await API.post(`/extract-deadlines/${id}`);
      toast.success(`${r.data.created} termen(e) extras(e) automat din documente`);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Nu s-au gasit termene in documente'); }
    finally { setExtractingDeadlines(false); }
  };
  const handleExportIcs = async () => {
    try {
      const res = await API.get(`/calendar/project/${id}/ics`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'termene.ics'; a.click();
      toast.success('Calendar (.ics) exportat - importa in Google/Outlook/Apple');
    } catch { toast.error('Eroare export calendar'); }
  };
  const googleCalUrl = (dl) => {
    const d = (dl.date || '').replace(/-/g, '');
    if (d.length !== 8) return '#';
    const next = new Date(dl.date); next.setDate(next.getDate() + 1);
    const dEnd = next.toISOString().slice(0, 10).replace(/-/g, '');
    const text = encodeURIComponent(`${project?.name || ''} - ${dl.title}`);
    const det = encodeURIComponent(dl.description || '');
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${d}/${dEnd}&details=${det}`;
  };

  // Team
  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    try { await API.post(`/projects/${id}/team`, { email: inviteEmail, role: inviteRole }); toast.success('Membru invitat!'); setInviteEmail(''); loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Eroare'); }
  };
  const handleRemoveMember = async (memberId) => {
    try { await API.delete(`/projects/${id}/team/${memberId}`); toast.success('Eliminat'); loadAll(); } catch { toast.error('Eroare'); }
  };

  // Partners
  const handleAddPartner = async () => {
    if (!partnerForm.company_name.trim()) { toast.error('Denumirea este obligatorie'); return; }
    try { await API.post('/partners', { ...partnerForm, project_id: id }); toast.success('Partener adaugat'); setPartnerForm({ partner_type: 'subcontractant', company_name: '', cui: '', role_description: '', percentage: 0 }); loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Eroare'); }
  };
  const handleDeletePartner = async (pId) => { try { await API.delete(`/partners/${pId}`); toast.success('Eliminat'); loadAll(); } catch {} };

  if (loading) return <div className="p-8"><div className="h-64 bg-white/5 animate-pulse rounded-lg" /></div>;
  if (!project) return null;

  const latestScoring = scoring[0]?.scoring;
  const latestScoring7 = scoring7[0]?.scoring;

  return (
    <div className="p-8" data-testid="project-detail-page">
      <button onClick={() => navigate('/projects')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors" data-testid="back-to-projects">
        <ArrowLeft className="w-4 h-4" />Inapoi la Proiecte
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>{project.name}</h1>
            <Badge variant="outline" className={`${moduleColors[project.module_type]} ring-1 ring-inset border-0 text-xs`}>{moduleLabels[project.module_type]}</Badge>
          </div>
          {project.description && <p className="text-sm text-muted-foreground">{project.description}</p>}
          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
            {project.estimated_value > 0 && <span className="font-mono-data">{project.estimated_value.toLocaleString()} {project.currency}</span>}
            {project.deadline && <span className="text-amber-400 font-mono-data">Termen: {project.deadline}</span>}
            <span><Users className="w-3 h-3 inline mr-1" />{team.members?.length || 0} membri</span>
            <span><Handshake className="w-3 h-3 inline mr-1" />{partners.length} parteneri</span>
          </div>
        </div>
        {project.win_score > 0 && (
          <div className="text-center px-4">
            <p className="text-3xl font-bold text-emerald-400 font-mono-data">{project.win_score}%</p>
            <p className="text-xs text-muted-foreground">Scor castig</p>
            <Progress value={project.win_score} className="w-24 mt-2 h-1.5" />
          </div>
        )}
      </div>

      <Tabs defaultValue="documents" className="w-full">
        <TabsList className="bg-zinc-900/50 border border-white/10 mb-6 flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="documents" data-testid="tab-documents"><FileText className="w-3.5 h-3.5 mr-1.5" />Documente</TabsTrigger>
          <TabsTrigger value="analysis" data-testid="tab-analysis"><Brain className="w-3.5 h-3.5 mr-1.5" />Analiza AI</TabsTrigger>
          <TabsTrigger value="forms" data-testid="tab-forms"><FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />Formulare</TabsTrigger>
          <TabsTrigger value="chat" data-testid="tab-chat"><MessageSquare className="w-3.5 h-3.5 mr-1.5" />Chat AI</TabsTrigger>
          <TabsTrigger value="deadlines" data-testid="tab-deadlines"><CalendarDays className="w-3.5 h-3.5 mr-1.5" />Termene</TabsTrigger>
          <TabsTrigger value="tools" data-testid="tab-tools"><Wrench className="w-3.5 h-3.5 mr-1.5" />Instrumente</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team"><Users className="w-3.5 h-3.5 mr-1.5" />Echipa</TabsTrigger>
        </TabsList>

        {/* ===== DOCUMENTS TAB - 2 ZONES ===== */}
        <TabsContent value="documents">
          <div className="space-y-6">
            {/* AI Auto-Generate / Verify Button */}
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold" style={{ fontFamily: 'Outfit' }}>
                    {project.module_type === 'autoritate' ? 'Verificare Complianta Oferta' : 'Generare Automata Oferta'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {project.module_type === 'autoritate'
                      ? 'AI verifica complianta documentelor ofertantului vs cerintele din procedura + genereaza intrebari clarificare'
                      : 'AI compara documentele autoritatii cu ale ofertantului, genereaza oferta tehnica, checklist cu atentionari, identifica lipsuri'}
                  </p>
                </div>
                <Button onClick={handleAutoGenerate} disabled={autoGenerating || documents.filter(d => d.doc_category === 'autoritate').length === 0} data-testid="auto-generate-btn">
                  {autoGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
                  {autoGenerating ? 'Se proceseaza...' : project.module_type === 'autoritate' ? 'Verifica Complianta' : 'Genereaza Automat'}
                </Button>
              </CardContent>
            </Card>

            {/* Zone 1: Authority Documents */}
            <Card className="bg-card border-white/10">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                    <Landmark className="w-4 h-4 text-blue-400" />
                    {project.module_type === 'autoritate' ? 'Documente Procedura (CS, Fisa Date, Criterii)' :
                     project.module_type === 'privat' ? 'Documente Client (RFP/RFQ)' :
                     'Documente Autoritate Contractanta (CS, Fisa Date)'}
                  </CardTitle>
                  {project.seap_url && (
                    <a href={project.seap_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary hover:underline flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" />Deschide pe SEAP
                    </a>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {/* SICAP Download Section */}
                <SicapDownloader projectId={id} onDownloaded={loadAll} />
                <div onClick={() => fileInputRef.current?.click()} className="border-2 border-dashed border-blue-500/20 hover:border-blue-500/50 rounded-lg p-6 text-center cursor-pointer transition-colors mb-4" data-testid="upload-zone-auth">
                  <Upload className={`w-6 h-6 mx-auto mb-2 ${uploading ? 'animate-bounce text-blue-400' : 'text-blue-400/50'}`} strokeWidth={1.5} />
                  <p className="text-xs font-medium text-blue-400">Incarca documente autoritate</p>
                  <p className="text-[10px] text-muted-foreground">Caiet de sarcini, Fisa de Date, Formulare, Criterii evaluare</p>
                  <input ref={fileInputRef} type="file" accept=".pdf,.txt,.csv,.md,.doc,.docx,.xlsx,.png,.jpg" multiple onChange={e => handleUpload(e, 'autoritate')} className="hidden" />
                </div>
                {documents.filter(d => d.doc_category === 'autoritate').map(doc => (
                  <div key={doc.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-blue-500/5 border border-blue-500/10 mb-2">
                    <FileText className="w-4 h-4 text-blue-400 shrink-0" strokeWidth={1.5} />
                    <div className="flex-1 min-w-0"><p className="text-xs font-medium truncate">{doc.filename}</p></div>
                    {doc.ocr_used && <Badge variant="outline" className="text-[9px] bg-cyan-500/10 text-cyan-400 border-0">OCR</Badge>}
                    <Badge variant="outline" className={`text-[9px] border-0 ${doc.status === 'procesat' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>{doc.status === 'procesat' ? 'OK' : '...'}</Badge>
                    <button onClick={() => handleDeleteDoc(doc.id)} className="p-1 rounded hover:bg-rose-500/10 text-muted-foreground hover:text-rose-400"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
                {!documents.filter(d => d.doc_category === 'autoritate').length && <p className="text-center text-[11px] text-muted-foreground py-2">Niciun document al autoritatii</p>}
              </CardContent>
            </Card>

            {/* Zone 2: Ofertant Documents */}
            <Card className="bg-card border-white/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  <Building2 className="w-4 h-4 text-emerald-400" />
                  {project.module_type === 'autoritate' ? 'Documente Ofertant (Oferta primita)' :
                   project.module_type === 'privat' ? 'Documente Proprii (Raspuns RFP)' :
                   'Documente Firma Ofertanta (Certificari, CV-uri, Experienta)'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div onClick={() => fileInputRef2.current?.click()} className="border-2 border-dashed border-emerald-500/20 hover:border-emerald-500/50 rounded-lg p-6 text-center cursor-pointer transition-colors mb-4" data-testid="upload-zone-offer">
                  <Upload className={`w-6 h-6 mx-auto mb-2 ${uploading ? 'animate-bounce text-emerald-400' : 'text-emerald-400/50'}`} strokeWidth={1.5} />
                  <p className="text-xs font-medium text-emerald-400">
                    {project.module_type === 'autoritate' ? 'Incarca oferta primita' : 'Incarca documentele tale'}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {project.module_type === 'autoritate' ? 'Oferta tehnica, Formulare completate, DUAE, CV-uri' : 'Certificari, Bilant, CV-uri, Experienta similara, Referinte'}
                  </p>
                  <input ref={fileInputRef2} type="file" accept=".pdf,.txt,.csv,.md,.doc,.docx,.xlsx,.png,.jpg" multiple onChange={e => handleUpload(e, 'ofertant')} className="hidden" />
                </div>
                {documents.filter(d => d.doc_category === 'ofertant' || !d.doc_category).map(doc => (
                  <div key={doc.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-emerald-500/5 border border-emerald-500/10 mb-2">
                    <FileText className="w-4 h-4 text-emerald-400 shrink-0" strokeWidth={1.5} />
                    <div className="flex-1 min-w-0"><p className="text-xs font-medium truncate">{doc.filename}</p></div>
                    {doc.ocr_used && <Badge variant="outline" className="text-[9px] bg-cyan-500/10 text-cyan-400 border-0">OCR</Badge>}
                    <Badge variant="outline" className={`text-[9px] border-0 ${doc.status === 'procesat' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>{doc.status === 'procesat' ? 'OK' : '...'}</Badge>
                    <button onClick={() => handleDeleteDoc(doc.id)} className="p-1 rounded hover:bg-rose-500/10 text-muted-foreground hover:text-rose-400"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
                {!documents.filter(d => d.doc_category === 'ofertant' || !d.doc_category).length && <p className="text-center text-[11px] text-muted-foreground py-2">Niciun document al ofertantului</p>}
              </CardContent>
            </Card>

            {/* AI Results */}
            {aiResults.length > 0 && (
              <Card className="bg-card border-white/10">
                <CardHeader><CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><Brain className="w-4 h-4 text-primary" />Rezultate AI</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {aiResults.slice(0, 3).map(r => {
                    const d = r.data || {};
                    return (
                      <div key={r.id} className="border border-white/5 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground">
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                          {r.type === 'verify_compliance' ? 'Verificare Complianta' : 'Generare Automata'} - {new Date(r.created_at).toLocaleString('ro-RO')}
                          {d.compliance_score && <Badge className="bg-primary/10 text-primary text-[10px]">{d.compliance_score}%</Badge>}
                          {d.score_estimate && <Badge className="bg-emerald-500/10 text-emerald-400 text-[10px]">{d.score_estimate}% scor</Badge>}
                        </div>
                        {d.summary && <p className="text-sm mb-3 p-3 rounded bg-zinc-900/50 border border-white/5">{d.summary}</p>}
                        {d.evaluation_summary && <p className="text-sm mb-3 p-3 rounded bg-zinc-900/50 border border-white/5">{d.evaluation_summary}</p>}
                        {d.recommendation && <p className="text-sm font-medium mb-3 text-primary">Recomandare: {d.recommendation}</p>}
                        {d.checklist_warnings?.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-semibold text-amber-400 mb-1">Atentionari ({d.checklist_warnings.length})</p>
                            {d.checklist_warnings.map((w, i) => (
                              <div key={i} className={`text-xs p-2 rounded mb-1 ${w.severity === 'critical' ? 'bg-rose-500/10 text-rose-300' : w.severity === 'warning' ? 'bg-amber-500/10 text-amber-300' : 'bg-blue-500/10 text-blue-300'}`}>
                                <span className="font-medium">{w.severity === 'critical' ? 'CRITIC' : w.severity === 'warning' ? 'ATENTIE' : 'INFO'}:</span> {w.item} - {w.details}
                              </div>
                            ))}
                          </div>
                        )}
                        {d.missing_documents?.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-semibold text-rose-400 mb-1">Documente Lipsa</p>
                            {d.missing_documents.map((m, i) => <p key={i} className="text-xs text-muted-foreground">- {m}</p>)}
                          </div>
                        )}
                        {d.clarification_questions?.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-semibold text-primary mb-1">Intrebari de Clarificare ({d.clarification_questions.length})</p>
                            {d.clarification_questions.map((q, i) => (
                              <div key={i} className="text-xs p-2 rounded bg-primary/5 border border-primary/10 mb-1">
                                <p className="font-medium text-foreground">{i+1}. {q.question}</p>
                                <p className="text-muted-foreground mt-0.5">Motiv: {q.reason} | Ref: {q.reference}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        {d.oferta_tehnica && (
                          <details className="mt-2">
                            <summary className="text-xs text-primary cursor-pointer hover:underline">Oferta Tehnica Generata (click pentru detalii)</summary>
                            <div className="prose-dark mt-2 p-3 rounded bg-zinc-900/30 border border-white/5 max-h-[400px] overflow-auto">
                              <ReactMarkdown>{d.oferta_tehnica}</ReactMarkdown>
                            </div>
                          </details>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ===== ANALYSIS + SCORING TAB ===== */}
        <TabsContent value="analysis">
          <div className="space-y-6">
            {/* 7-Factor ML Scoring */}
            <Card className="bg-gradient-to-br from-primary/10 to-transparent border-primary/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><Brain className="w-4 h-4 text-primary" />Scoring ML - Model cu 7 Factori</h3>
                    <p className="text-xs text-muted-foreground mt-1">Eligibilitate 20% · Tehnic 25% · Financiar 15% · Performance 15% · Competitie 10% · Documente 10% · Pret 5%</p>
                  </div>
                  <Button onClick={handleScoring7} disabled={scoring7Loading || documents.length === 0} data-testid="scoring7-btn">
                    {scoring7Loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Se calculeaza...</> : <><Brain className="w-4 h-4 mr-2" />Calculeaza Scor ML</>}
                  </Button>
                </div>
                {latestScoring7 ? (
                  <div>
                    <div className="flex items-center gap-6 mb-4 p-4 rounded-lg bg-zinc-900/50 border border-white/5">
                      <div className="text-center shrink-0">
                        <p className={`text-5xl font-bold font-mono-data ${latestScoring7.scor_total >= 70 ? 'text-emerald-400' : latestScoring7.scor_total >= 40 ? 'text-amber-400' : 'text-rose-400'}`} data-testid="scoring7-total">{latestScoring7.scor_total}%</p>
                        <p className="text-xs text-muted-foreground">Scor Ponderat</p>
                      </div>
                      <div className="flex-1 space-y-1.5">
                        {latestScoring7.categorii?.map((cat, i) => (
                          <div key={i} className="flex items-center gap-3" data-testid={`scoring7-factor-${cat.key}`}>
                            <span className="text-[11px] text-muted-foreground w-40 shrink-0 truncate">{cat.nume} <span className="text-[9px] opacity-60">({cat.pondere}%)</span></span>
                            <div className="flex-1"><Progress value={cat.scor} className="h-1.5" /></div>
                            <span className={`text-xs font-bold font-mono-data w-10 text-right ${cat.scor >= 70 ? 'text-emerald-400' : cat.scor >= 40 ? 'text-amber-400' : 'text-rose-400'}`}>{cat.scor}%</span>
                            <span className="text-[10px] text-primary font-mono-data w-10 text-right">+{cat.contributie}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    {latestScoring7.recomandare && <p className="text-sm mb-3 p-3 rounded bg-primary/5 border border-primary/10"><span className="font-semibold text-primary">Recomandare:</span> {latestScoring7.recomandare}</p>}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {latestScoring7.puncte_tari?.length > 0 && (
                        <div><p className="text-xs font-semibold text-emerald-400 mb-1">Puncte Tari</p>
                          {latestScoring7.puncte_tari.map((p, i) => <p key={i} className="text-xs text-muted-foreground ml-3">+ {p}</p>)}</div>
                      )}
                      {latestScoring7.puncte_slabe?.length > 0 && (
                        <div><p className="text-xs font-semibold text-rose-400 mb-1">Puncte Slabe</p>
                          {latestScoring7.puncte_slabe.map((p, i) => <p key={i} className="text-xs text-muted-foreground ml-3">- {p}</p>)}</div>
                      )}
                    </div>
                    {latestScoring7.actiuni_prioritare?.length > 0 && (
                      <div className="mt-3"><p className="text-xs font-semibold text-amber-400 mb-1 flex items-center gap-1"><TrendingUp className="w-3 h-3" />Actiuni Prioritare</p>
                        {latestScoring7.actiuni_prioritare.map((a, i) => <p key={i} className="text-xs text-muted-foreground ml-3">{i + 1}. {a}</p>)}</div>
                    )}
                    {latestScoring7.categorii?.some(c => c.detalii) && (
                      <details className="mt-3"><summary className="text-xs text-primary cursor-pointer hover:underline">Detalii per factor</summary>
                        <div className="mt-2 space-y-1">
                          {latestScoring7.categorii.filter(c => c.detalii).map((c, i) => <p key={i} className="text-[11px] text-muted-foreground"><span className="text-foreground font-medium">{c.nume}:</span> {c.detalii}</p>)}
                        </div>
                      </details>
                    )}
                  </div>
                ) : <p className="text-xs text-muted-foreground text-center py-4">Incarca documente si apasa "Calculeaza Scor ML" - AI foloseste si licitatiile castigate din Baza de Cunostinte ca referinta</p>}
              </CardContent>
            </Card>
            {/* Scoring Section */}
            <Card className="bg-card border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><BarChart3 className="w-4 h-4 text-amber-400" />Scoring Comparativ</h3>
                    <p className="text-xs text-muted-foreground mt-1">Compara profilul firmei cu cerintele licitatiei</p>
                  </div>
                  <Button onClick={handleScoring} disabled={scoringLoading || documents.length === 0} variant="outline" data-testid="scoring-btn">
                    {scoringLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Se calculeaza...</> : <><BarChart3 className="w-4 h-4 mr-2" />Calculeaza Scor</>}
                  </Button>
                </div>
                {latestScoring ? (
                  <div>
                    <div className="flex items-center gap-4 mb-4 p-4 rounded-lg bg-zinc-900/50 border border-white/5">
                      <div className="text-center">
                        <p className={`text-4xl font-bold font-mono-data ${latestScoring.scor_total >= 70 ? 'text-emerald-400' : latestScoring.scor_total >= 40 ? 'text-amber-400' : 'text-rose-400'}`}>{latestScoring.scor_total}%</p>
                        <p className="text-xs text-muted-foreground">Scor Total</p>
                      </div>
                      <div className="flex-1 grid grid-cols-2 lg:grid-cols-3 gap-2">
                        {latestScoring.categorii?.map((cat, i) => (
                          <div key={i} className="p-2 rounded bg-zinc-800/50 border border-white/5">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] text-muted-foreground truncate">{cat.nume}</span>
                              <span className={`text-xs font-bold font-mono-data ${cat.scor >= 70 ? 'text-emerald-400' : cat.scor >= 40 ? 'text-amber-400' : 'text-rose-400'}`}>{cat.scor}%</span>
                            </div>
                            <Progress value={cat.scor} className="h-1" />
                            {cat.status === 'critic' && <p className="text-[9px] text-rose-400 mt-1 truncate">{cat.recomandari}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                    {latestScoring.puncte_slabe?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-rose-400 mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Puncte Slabe</p>
                        {latestScoring.puncte_slabe.map((p, i) => <p key={i} className="text-xs text-muted-foreground ml-4">- {p}</p>)}
                      </div>
                    )}
                    {latestScoring.actiuni_prioritare?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-amber-400 mb-1 flex items-center gap-1"><TrendingUp className="w-3 h-3" />Actiuni Prioritare</p>
                        {latestScoring.actiuni_prioritare.map((a, i) => <p key={i} className="text-xs text-muted-foreground ml-4">- {a}</p>)}
                      </div>
                    )}
                  </div>
                ) : <p className="text-xs text-muted-foreground text-center py-4">Incarca documente si apasa "Calculeaza Scor"</p>}
              </CardContent>
            </Card>
            {/* Analysis Section */}
            <Card className="bg-card border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-semibold" style={{ fontFamily: 'Outfit' }}>Analiza AI</h3>
                  <Button onClick={handleAnalyze} disabled={analyzing || documents.length === 0} data-testid="analyze-btn">
                    {analyzing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Se analizeaza...</> : <><Brain className="w-4 h-4 mr-2" />Analizeaza</>}
                  </Button>
                </div>
                {analyses.map(a => (
                  <div key={a.id} className="border border-white/5 rounded-lg p-6 mb-4">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs text-muted-foreground"><CheckCircle className="w-3.5 h-3.5 text-emerald-400 inline mr-1" />{new Date(a.created_at).toLocaleString('ro-RO')}</span>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleExport(a.id, 'pdf')} className="text-xs h-7"><Download className="w-3 h-3 mr-1" />PDF</Button>
                        <Button size="sm" variant="outline" onClick={() => handleExport(a.id, 'docx')} className="text-xs h-7"><Download className="w-3 h-3 mr-1" />Word</Button>
                      </div>
                    </div>
                    <div className="prose-dark"><ReactMarkdown>{a.result}</ReactMarkdown></div>
                  </div>
                ))}
                {!analyses.length && <p className="text-center text-sm text-muted-foreground py-6">Nicio analiza</p>}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ===== FORMS TAB (DUAE, Fisa Date, Partners, F1-F6) ===== */}
        <TabsContent value="forms">
          <div className="space-y-6">
            <SuggestedResponses projectId={project.id} />
            {/* Partners Section */}
            <Card className="bg-card border-white/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><Handshake className="w-4 h-4 text-cyan-400" />Parteneri / Subcontractanti / Asocieri</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-4">
                  <Select value={partnerForm.partner_type} onValueChange={v => setPartnerForm({...partnerForm, partner_type: v})}>
                    <SelectTrigger className="bg-zinc-900/50 border-white/10 h-9 text-sm" data-testid="partner-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{partnerTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input value={partnerForm.company_name} onChange={e => setPartnerForm({...partnerForm, company_name: e.target.value})} placeholder="Denumire companie *" className="bg-zinc-900/50 border-white/10 h-9 text-sm" data-testid="partner-name" />
                  <Input value={partnerForm.cui} onChange={e => setPartnerForm({...partnerForm, cui: e.target.value})} placeholder="CUI" className="bg-zinc-900/50 border-white/10 h-9 text-sm font-mono-data" />
                  <div className="flex gap-2">
                    <Input type="number" value={partnerForm.percentage} onChange={e => setPartnerForm({...partnerForm, percentage: parseFloat(e.target.value)||0})} placeholder="%" className="bg-zinc-900/50 border-white/10 h-9 text-sm w-20 font-mono-data" />
                    <Button size="sm" onClick={handleAddPartner} className="h-9" data-testid="add-partner-btn">Adauga</Button>
                  </div>
                </div>
                {partners.map(p => (
                  <div key={p.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-zinc-900/50 border border-white/5 mb-2" data-testid={`partner-${p.id}`}>
                    <Badge variant="outline" className="text-[10px] bg-cyan-500/10 text-cyan-400 ring-cyan-500/20 ring-1 ring-inset border-0 shrink-0">{partnerTypes.find(t=>t.value===p.partner_type)?.label || p.partner_type}</Badge>
                    <span className="text-sm font-medium">{p.company_name}</span>
                    {p.cui && <span className="text-xs text-muted-foreground font-mono-data">CUI: {p.cui}</span>}
                    {p.percentage > 0 && <span className="text-xs text-amber-400 font-mono-data">{p.percentage}%</span>}
                    <button onClick={() => handleDeletePartner(p.id)} className="ml-auto p-1 rounded hover:bg-rose-500/10 text-muted-foreground hover:text-rose-400"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
                {!partners.length && <p className="text-xs text-muted-foreground text-center py-2">Niciun partener adaugat</p>}
              </CardContent>
            </Card>

            {/* DUAE + Fisa Date (Fisa Date DOAR pentru autoritate) */}
            <div className={`grid grid-cols-1 ${project.module_type === 'autoritate' ? 'lg:grid-cols-2' : ''} gap-4`}>
              <Card className="bg-card border-white/10">
                <CardContent className="p-6 text-center">
                  <FileSpreadsheet className="w-8 h-8 mx-auto text-primary mb-2" strokeWidth={1.5} />
                  <h3 className="font-semibold text-sm mb-1" style={{ fontFamily: 'Outfit' }}>DUAE</h3>
                  <p className="text-xs text-muted-foreground mb-3">Include parteneri/subcontractanti</p>
                  <Button onClick={() => handleGenerateForm('duae')} disabled={!!generatingForm || documents.length === 0} className="w-full" data-testid="generate-duae-btn">
                    {generatingForm === 'duae' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}{generatingForm === 'duae' ? 'Se genereaza...' : 'Genereaza DUAE'}
                  </Button>
                </CardContent>
              </Card>
              {project.module_type === 'autoritate' && (
                <Card className="bg-card border-white/10">
                  <CardContent className="p-6 text-center">
                    <FileText className="w-8 h-8 mx-auto text-amber-400 mb-2" strokeWidth={1.5} />
                    <h3 className="font-semibold text-sm mb-1" style={{ fontFamily: 'Outfit' }}>Fisa de Date</h3>
                    <p className="text-xs text-muted-foreground mb-3">Fisa de Date a Achizitiei</p>
                    <Button onClick={() => handleGenerateForm('fisa-date')} disabled={!!generatingForm || documents.length === 0} variant="outline" className="w-full" data-testid="generate-fisa-btn">
                      {generatingForm === 'fisa-date' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}{generatingForm === 'fisa-date' ? 'Se genereaza...' : 'Genereaza Fisa de Date'}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Smart Form Generation */}
            <Card className="bg-card border-white/10">
              <CardHeader className="pb-3">
                <CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>{project.module_type === 'privat' ? 'Documente Raspuns (RFI / RFP / RFQ)' : 'Formulare, Devize si Situatii de Lucrari'}</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">{project.module_type === 'privat' ? 'AI genereaza raspunsul adaptat cererii clientului privat, folosind experienta SAGA' : 'AI invata din documentele incarcate si genereaza automat'}</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {(project.module_type === 'privat' ? [
                    { code: 'raspuns_rfi', icon: '📥', name: 'Raspuns RFI', desc: 'Prezentare firma, capabilitati, referinte, informatii de piata (fara preturi ferme)', color: 'bg-cyan-500/10 border-cyan-500/30' },
                    { code: 'propunere_rfp', icon: '📋', name: 'Propunere RFP', desc: 'Solutie tehnica, metodologie, grafic, echipa, propunere comerciala', color: 'bg-blue-500/10 border-blue-500/30' },
                    { code: 'oferta_rfq', icon: '💰', name: 'Oferta RFQ', desc: 'Centralizator preturi pe categorii, TVA, termen valabilitate, conditii plata', color: 'bg-emerald-500/10 border-emerald-500/30' },
                    { code: 'riscuri', icon: '⚠️', name: 'Analiza Riscuri', desc: 'Cerinte critice, termene, penalitati, clauze neclare', color: 'bg-rose-500/10 border-rose-500/30' },
                  ] : [
                    { code: 'auto', icon: '🤖', name: 'Generare Automata', desc: 'AI identifica si genereaza toate formularele necesare', color: 'bg-primary/10 border-primary/30' },
                    { code: 'oferta_tehnica', icon: '📋', name: 'Oferta Tehnica (Draft)', desc: 'Metodologie, organizare santier, plan calitate, grafic', color: 'bg-blue-500/10 border-blue-500/30' },
                    { code: 'oferta_financiara', icon: '💰', name: 'Oferta Financiara (Draft)', desc: 'Deviz general, centralizator preturi pe categorii', color: 'bg-emerald-500/10 border-emerald-500/30' },
                    { code: 'devize', icon: '📊', name: 'Devize si Situatii Lucrari', desc: 'F3 + C6 (materiale) + C7 (manopera) + C8 (utilaje) + C9 (transport)', color: 'bg-amber-500/10 border-amber-500/30' },
                    { code: 'formulare', icon: '📄', name: 'Formulare Standard', desc: 'Scrisoare oferta, declaratii, DUAE, garantii, experienta', color: 'bg-purple-500/10 border-purple-500/30' },
                    { code: 'riscuri', icon: '⚠️', name: 'Analiza Riscuri', desc: 'Cerinte critice, termene, penalitati, clauze neclare', color: 'bg-rose-500/10 border-rose-500/30' },
                  ]).map(btn => (
                    <button key={btn.code} onClick={() => handleGenerateTemplate(btn.code)} disabled={!!generatingForm || documents.filter(d => d.doc_category === 'autoritate').length === 0}
                      className={`p-4 rounded-lg border ${btn.color} hover:border-primary/50 transition-all text-left group disabled:opacity-40`} data-testid={`gen-${btn.code}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{btn.icon}</span>
                        <p className="text-sm font-semibold group-hover:text-foreground transition-colors">{btn.name}</p>
                      </div>
                      <p className="text-[10px] text-muted-foreground leading-tight">{btn.desc}</p>
                      {generatingForm === btn.code && <Loader2 className="w-3 h-3 animate-spin mt-2 text-primary" />}
                    </button>
                  ))}
                </div>
                {documents.filter(d => d.doc_category === 'autoritate').length === 0 && (
                  <p className="text-xs text-amber-400 text-center mt-3">{project.module_type === 'privat' ? 'Incarcati documentele clientului (RFI / RFP / RFQ) pentru a activa generarea' : 'Incarcati documente ale autoritatii contractante pentru a activa generarea'}</p>
                )}
              </CardContent>
            </Card>

            {/* Generated Forms */}
            {generatedForms.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold" style={{ fontFamily: 'Outfit' }}>Formulare Generate ({generatedForms.length})</h3>
                {generatedForms.map(f => (
                  <Card key={f.id} className="bg-card border-white/10">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary ring-primary/20 ring-1 ring-inset border-0">{f.form_type.toUpperCase()}</Badge>
                          <span className="text-xs text-muted-foreground">{new Date(f.created_at).toLocaleString('ro-RO')}</span>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(f.content); toast.success('Copiat!'); }} className="text-xs h-7">Copiaza</Button>
                      </div>
                    </CardHeader>
                    <CardContent><div className="prose-dark border border-white/5 rounded-lg p-4 max-h-[400px] overflow-auto bg-zinc-900/30"><ReactMarkdown>{f.content}</ReactMarkdown></div></CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ===== CHAT TAB ===== */}
        <TabsContent value="chat">
          <Card className="bg-card border-white/10">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>Chat cu Documentele</h3>
              <div className="h-[400px] overflow-auto border border-white/5 rounded-lg p-4 mb-4 bg-zinc-900/30" data-testid="chat-area">
                {!chatMessages.length && <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Pune intrebari despre documente...</div>}
                {chatMessages.map((msg, i) => (
                  <div key={i} className={`mb-4 ${msg.role === 'user' ? 'text-right' : ''}`}>
                    <div className={`inline-block max-w-[80%] p-3 rounded-lg text-sm ${msg.role === 'user' ? 'bg-primary/20' : 'bg-zinc-800'}`}>
                      {msg.role === 'assistant' ? <div className="prose-dark"><ReactMarkdown>{msg.text}</ReactMarkdown></div> : msg.text}
                    </div>
                  </div>
                ))}
                {chatLoading && <div className="mb-4"><div className="inline-block p-3 rounded-lg bg-zinc-800"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div></div>}
                <div ref={chatEndRef} />
              </div>
              <div className="flex gap-2">
                <Input value={chatInput} onChange={e => setChatInput(e.target.value)} placeholder="Intreaba ceva..." className="bg-zinc-900/50 border-white/10" onKeyDown={e => e.key === 'Enter' && handleChat()} data-testid="chat-input" />
                <Button onClick={handleChat} disabled={chatLoading || !chatInput.trim()} data-testid="chat-send-btn"><Send className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== TOOLS TAB (Checklist, Compliance Matrix, Gantt) ===== */}
        <TabsContent value="tools">
          <div className="space-y-8">
            <ChecklistTab projectId={id} documents={documents} />
            <div className="border-t border-white/10 pt-8">
              <ComplianceMatrixTab projectId={id} documents={documents} />
            </div>
            <div className="border-t border-white/10 pt-8">
              <GanttChartTab projectId={id} documents={documents} />
            </div>
          </div>
        </TabsContent>

        {/* ===== DEADLINES TAB ===== */}
        <TabsContent value="deadlines">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2 bg-card border-white/10">
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Termene</CardTitle>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={handleExportIcs} disabled={!deadlines.length} className="text-xs h-8" data-testid="export-ics-btn">
                    <CalendarDays className="w-3.5 h-3.5 mr-1.5" />Export .ics
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleExtractDeadlines} disabled={extractingDeadlines || documents.filter(d => d.doc_category === 'autoritate').length === 0} className="text-xs h-8" data-testid="extract-deadlines-btn">
                    {extractingDeadlines ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Brain className="w-3.5 h-3.5 mr-1.5" />}
                    {extractingDeadlines ? 'Se extrag...' : 'Extrage termene din documente'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {deadlines.map(dl => (
                  <div key={dl.id} className="flex items-center gap-3 p-3 rounded-lg bg-zinc-900/50 border border-white/5 mb-2">
                    <button onClick={() => handleToggleDeadline(dl)} className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${dl.completed ? 'bg-emerald-500 border-emerald-500' : 'border-white/20 hover:border-primary'}`}>{dl.completed && <CheckCircle className="w-3 h-3 text-white" />}</button>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${dl.completed ? 'line-through text-muted-foreground' : ''}`}>{dl.title}</p>
                      {dl.description && <p className="text-[10px] text-muted-foreground truncate">{dl.description}</p>}
                    </div>
                    {dl.auto_extracted && <Badge variant="outline" className="text-[9px] bg-primary/10 text-primary border-0 shrink-0">AI</Badge>}
                    <Badge variant="outline" className={`text-[10px] ${dl.priority === 'high' ? 'bg-rose-500/10 text-rose-400' : dl.priority === 'medium' ? 'bg-amber-500/10 text-amber-400' : 'bg-emerald-500/10 text-emerald-400'} ring-1 ring-inset border-0`}>{dl.priority === 'high' ? 'Urgent' : dl.priority === 'medium' ? 'Mediu' : 'Scazut'}</Badge>
                    <span className="text-xs font-mono-data text-amber-400">{dl.date}</span>
                    <a href={googleCalUrl(dl)} target="_blank" rel="noopener noreferrer" title="Adauga in Google Calendar" className="p-1 rounded hover:bg-blue-500/10 text-muted-foreground hover:text-blue-400" data-testid={`gcal-${dl.id}`}><CalendarPlus className="w-3.5 h-3.5" /></a>
                    <button onClick={() => handleDeleteDeadline(dl.id)} className="p-1 rounded hover:bg-rose-500/10 text-muted-foreground hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                {!deadlines.length && <p className="text-center text-sm text-muted-foreground py-4">Niciun termen. Incarca documentele autoritatii - termenele se extrag automat.</p>}
              </CardContent>
            </Card>
            <Card className="bg-card border-white/10">
              <CardHeader><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Adauga Termen</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div><Label className="text-xs">Titlu *</Label><Input value={dlForm.title} onChange={e => setDlForm({...dlForm, title: e.target.value})} className="mt-1 bg-zinc-900/50 border-white/10 h-9 text-sm" data-testid="deadline-title-input" /></div>
                <div><Label className="text-xs">Data *</Label><Input type="date" value={dlForm.date} onChange={e => setDlForm({...dlForm, date: e.target.value})} className="mt-1 bg-zinc-900/50 border-white/10 h-9 text-sm font-mono-data" data-testid="deadline-date-input" /></div>
                <div><Label className="text-xs">Prioritate</Label>
                  <Select value={dlForm.priority} onValueChange={v => setDlForm({...dlForm, priority: v})}>
                    <SelectTrigger className="mt-1 bg-zinc-900/50 border-white/10 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="high">Urgent</SelectItem><SelectItem value="medium">Mediu</SelectItem><SelectItem value="low">Scazut</SelectItem></SelectContent>
                  </Select></div>
                <Button onClick={handleAddDeadline} className="w-full" size="sm" data-testid="add-deadline-btn"><CalendarDays className="w-3.5 h-3.5 mr-1.5" />Adauga</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ===== TEAM TAB ===== */}
        <TabsContent value="team">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2 bg-card border-white/10">
              <CardHeader><CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><Users className="w-4 h-4 text-primary" />Echipa Proiect</CardTitle></CardHeader>
              <CardContent>
                {team.owner && (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-900/50 border border-white/5 mb-2" data-testid="team-owner">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-medium text-primary">{team.owner.name?.charAt(0)?.toUpperCase()}</div>
                    <div className="flex-1"><p className="text-sm font-medium">{team.owner.name}</p><p className="text-xs text-muted-foreground">{team.owner.email}</p></div>
                    <Badge className="bg-primary/10 text-primary text-[10px]">Proprietar</Badge>
                  </div>
                )}
                {team.members?.map(m => (
                  <div key={m.id} className="flex items-center gap-3 p-3 rounded-lg bg-zinc-900/50 border border-white/5 mb-2" data-testid={`team-member-${m.id}`}>
                    <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center text-sm font-medium">{m.name?.charAt(0)?.toUpperCase() || '?'}</div>
                    <div className="flex-1"><p className="text-sm font-medium">{m.name || m.email}</p><p className="text-xs text-muted-foreground">{m.email}</p></div>
                    <Badge variant="outline" className="text-[10px] bg-zinc-800 border-0">{m.role === 'admin' ? 'Admin' : 'Membru'}</Badge>
                    <button onClick={() => handleRemoveMember(m.id)} className="p-1 rounded hover:bg-rose-500/10 text-muted-foreground hover:text-rose-400"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                {!team.members?.length && <p className="text-xs text-muted-foreground text-center py-4 mt-2">Niciun membru invitat</p>}
              </CardContent>
            </Card>
            <Card className="bg-card border-white/10">
              <CardHeader><CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><UserPlus className="w-4 h-4 text-emerald-400" />Invita Membru</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div><Label className="text-xs">Email *</Label><Input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="coleg@firma.ro" className="mt-1 bg-zinc-900/50 border-white/10 h-9 text-sm" data-testid="invite-email" /></div>
                <div><Label className="text-xs">Rol</Label>
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger className="mt-1 bg-zinc-900/50 border-white/10 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="member">Membru</SelectItem><SelectItem value="viewer">Vizualizare</SelectItem></SelectContent>
                  </Select></div>
                <Button onClick={handleInvite} className="w-full" size="sm" data-testid="invite-btn"><UserPlus className="w-3.5 h-3.5 mr-1.5" />Invita</Button>
                <p className="text-[10px] text-muted-foreground">Utilizatorul trebuie sa aiba cont SINSOL</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
